// © Microsoft Corporation. All rights reserved.

import { IsForceTimestampExpired, getRenderCacheKeys } from "./PersistHelpers";
import { RenderCache, RenderCacheKeys } from "./RenderCache";
import { RenderCacheErrors, logger } from "@msnews/diagnostics";
import { appEnvironment, getLocalStorage } from "@msnews/core";
import { batchCSS, renderCacheKeyOfNormalizedKeys, scrapeDom, windowAvailable } from "./Helper";
import { defaultExpirationTimeMs, expirationPrefix, forceTimestampPrefix, jssMapSuffix, keysPrefix, storagePrefix } from "./Constants";

import { RenderCacheConfig } from "./RenderCacheConfig";
import { Telemetry } from "@msnews/telemetry-contracts";
import { userInteractionHappened } from "./UserInteractionWatcher";

const jssGeneratedClassesMap: { [instanceId: string]: Array<string> } = {};
let jssCaptureCompleted = false;

/**
 * Persist the complete render state (DOM and Styles) necessary to restore the rendered Peregrine SPA page, without needing to
 * first load all of the Peregrine experience bundles.
 * This is the equivalent of using Server Side Rendering to generate the initial DOM, though in this case the initial DOM elements and styles
 * are persisted and loaded locally on the client.
 * @param config Render cache configuration.
 * @param stateJson The state to persist in JSON form.
 * @param markers List of performance markers, which will be fired upon cache restore.
 * @param invokeFallback Canvas specific fallback function to be executed on jss exception.
 */
export function persistRenderCache(config: RenderCacheConfig, stateJson?: string, markers?: string[], invokeFallback?: (message: string) => void): void {
    jssCaptureCompleted = true;

    if (!config || !getLocalStorage().supported || !window.renderCacheEnabled) {
        return;
    }

    // Get normalized render cache keys
    const renderCacheKeys: RenderCacheKeys = getRenderCacheKeys(config && config.normalizeKeyParamsList);

    // Check if we need to refresh cache by force. Used as Failsafe if user stuck in broken render cache loop.
    if (IsForceTimestampExpired(config && config.forceExpirationTime)) {
        removeRenderCache();
        logger.log("[RC Info] Force timestamp expired: removing render cache");
        return;
    }

    // Found an issue with User interaction check. Its inconsistent and as we are not deleting RC on this,
    // users may get stuck in this state.
    if (userInteractionHappened()) {
        return;
    }

    // When critical jss mismatch is detected - do not persist and based on config decide to purge and fallback
    if (criticalJssMismatchDetected(config, invokeFallback)) {
        return;
    }

    let { includeNewsFeed, expirationTime, newsFeedExpirationTime } = config;

    // Persist build version recorded for the page being render cached
    const buildVersion = appEnvironment.BuildVersion || "";

    // Form Cache Object
    const renderCacheStorageObj: RenderCache = {
        dom: scrapeDom(includeNewsFeed),
        batch_css: batchCSS(),
        build_version: buildVersion,
        markers,
        state: stateJson
    };

    expirationTime = (includeNewsFeed ? newsFeedExpirationTime : expirationTime)
        || defaultExpirationTimeMs;

    // Try to persist Render Cache and Timestamp
    const expiresOn: number = new Date().getTime() + expirationTime;

    getLocalStorage().setObject(renderCacheKeys.expn, expiresOn);
    getLocalStorage().setObject(renderCacheKeys.keyn, renderCacheStorageObj);
}

/**
 * Deletes render cache local storage for all domains
 */
export function removeRenderCache(): void {
    if (!getLocalStorage().supported) {
        return;
    }

    // The render cache Urls are currently not normalized, so we have different render
    // cache saved for DHP and NTP page. But, some of the properties (example: market)
    // is shared between those pages. To ensure a purge on render cache on one page,
    // does purge the other render cache as well, and avoid breaking the page layout,
    // we remove all render cache keys on removeRenderCache
    getLocalStorage().removeSubstringKeys(storagePrefix);
    getLocalStorage().removeSubstringKeys(expirationPrefix);
    getLocalStorage().removeSubstringKeys(forceTimestampPrefix);
    getLocalStorage().removeSubstringKeys(keysPrefix);
    getLocalStorage().removeSubstringKeys(jssMapSuffix);
}

/**
 * Deletes render cache local storage only for current href
 */
export function removeCurrentRenderCache(): void {
    if (!getLocalStorage().supported) {
        return;
    }

    const renderCacheKeys: RenderCacheKeys = getRenderCacheKeys();
    getLocalStorage().removeObject(renderCacheKeys.keyn);
    getLocalStorage().removeObject(renderCacheKeys.frcn);
    getLocalStorage().removeObject(renderCacheKeys.expn);
    getLocalStorage().removeObject(renderCacheKeys.jssn);
    getLocalStorage().removeObject(renderCacheKeyOfNormalizedKeys);
}

/**
 * Capture class names generated by JSS and maintain inside a dictionary.
 * @param key key for class name
 * @param val JSS val for class name
 */
export function captureJssGeneratedClasses(key: string, val: string): void {
    if (jssCaptureCompleted || !(windowAvailable && window.renderCacheEnabled)) {
        return;
    }

    if (jssGeneratedClassesMap[key]) {
        jssGeneratedClassesMap[key].push(val);
    } else {
        jssGeneratedClassesMap[key] = [val];
    }
}

/**
 * Compares stored JSS Map with current JSS Map and log corresponding app errors
 * @param config Render cache configuration.
 * @param invokeFallback Canvas specific fallback function to be executed on jss exception.
 * @returns boolean - returns true when there is critical jss mismatch, otherwise false
 */
export function criticalJssMismatchDetected(config: RenderCacheConfig, invokeFallback?: (message: string) => void): boolean {
    // if we haven't captured any generated classes log an app error and return
    if (Object.keys(jssGeneratedClassesMap).length === 0) {
        logJssMismatchAppError(RenderCacheErrors.JssStyleMismatchWarning, "Generated classes map is empty. ");
        return false;
    }

    const { isJssIdentical, isCriticalJssIdentical, jssWarningMessage, jssErrorMessage } = compareJssMap(config);

    // in case of non critical JSS mismatch we just log an app warning
    if (!isJssIdentical) {
        if (config && config.enableJssMismatchWarningLogs) {
            logJssMismatchAppError(RenderCacheErrors.JssStyleMismatchWarning, jssWarningMessage);
        }
    } else {
        // in case there are no mismatches - write back the newly generated class map back to localstorage
        Object.keys(jssGeneratedClassesMap).length && getLocalStorage().setObject(getRenderCacheKeys().jssn, jssGeneratedClassesMap);
        logger.log("[RC Info] No style mismatch found");
    }

    // in case of critical JSS mismatch, log an app error and based on config
    // decide to purge and invoke canvas specific fallback
    if (!isCriticalJssIdentical) {
        logJssMismatchAppError(RenderCacheErrors.JssStyleCriticalMismatchError, jssErrorMessage);

        if (config && config.enablePurgeOnJssMismatch) {
            removeRenderCache();

            if (config && config.enableFallbackOnJssMismatch && invokeFallback && typeof invokeFallback === "function") {
                invokeFallback(jssErrorMessage);
            }
        }
    }

    return !isCriticalJssIdentical;
}

/**
 * Compares stored JSS Map with current JSS Map and identifies if those are critical vs non critical error message with corresponding error message
 * @param config Render cache configuration.
 * @returns object - returns mismatch and error information
 */
function compareJssMap(config: RenderCacheConfig): { isJssIdentical: boolean, isCriticalJssIdentical: boolean, jssWarningMessage: string, jssErrorMessage: string } {
    let jssErrorMessage = "";
    let jssWarningMessage = "";
    let isCriticalJssIdentical = true;
    let isJssIdentical = true;

    const jssCriticalClassListMap: { [className: string]: boolean } = getJssCriticalClassListMap(config && config.jssCriticalClassList || []);

    const storedMap = getLocalStorage().getObject(getRenderCacheKeys().jssn);
    if (storedMap && typeof storedMap === "object" && Object.keys(storedMap).length) {
        // this for loop compares every item in stored Map of Css class names to the current page instance map
        for (const key in storedMap) {
            const storedClasses = storedMap[key];

            // if stored class isn't correctly set, log a non critical app app warning and return
            if (!Array.isArray(storedClasses)) {
                return { isJssIdentical: false, isCriticalJssIdentical: true, jssWarningMessage: `Stored classes expected to be an array, but received something else. stored val: ${storedClasses}. `, jssErrorMessage: "" };
            }

            /*
                We only care about extra classes being present in the stored classes. If the current page
                instance has new class names not present in the stored map, we do not consider this a mismatch
                since the render cache snapshot won't be broken due to this scenario. Also if there are no
                classes corresponding to a key in the current map, detect that as a mismatch as well.

                --- Scenario 1: (detect as a mismatch) ---
                storedClasses : k1: { [c1, c2, c3] }
                currentClasses: k1: { [c1, c2] }

                --- Scenario 2: (do not detect as a mismatch) ---
                storedClasses : k1: { [c1, c2] }
                currentClasses: k1: { [c1, c2, c3] }

                --- Scenario 3: (detect as a mismatch) ---
                storedClasses : k1: { [c1, c2] }
                currentClasses: k2: { [c3, c4, c5] }
            */
            const currentClasses = jssGeneratedClassesMap[key];
            if (currentClasses) {
                if (storedClasses.some(className => currentClasses.indexOf(className) === -1)) {
                    const errorMessage = `JSSError: val mismatch for key: ${key}, val stored: ${storedClasses}, new val: ${currentClasses}. `;
                    if (jssCriticalClassListMap[key]) {
                        jssErrorMessage += errorMessage;
                        isCriticalJssIdentical = false;
                    }

                    jssWarningMessage += errorMessage;
                    isJssIdentical = false;
                }
            } else {
                const errorMessage = `JSSError: val mismatch for key: ${key}, val stored: ${storedClasses}, new val: '' (empty). `;
                if (jssCriticalClassListMap[key]) {
                    jssErrorMessage += errorMessage;
                    isCriticalJssIdentical = false;
                }

                jssWarningMessage += errorMessage;
                isJssIdentical = false;
            }
        }
    }

    return {
        isJssIdentical: isJssIdentical,
        isCriticalJssIdentical: isCriticalJssIdentical,
        jssWarningMessage: jssWarningMessage,
        jssErrorMessage: jssErrorMessage
    };
}

/**
 * Helper function to log render cache jss mismatch app errors
 * @param errorParams The error params.
 * @param customMessage The custom message to be logged.
 */
function logJssMismatchAppError(errorParams: any, customMessage: string): void {
    Telemetry.sendAppErrorEvent({
        ...errorParams,
        message: "RC JSS mismatch issue",
        pb: {
            ...errorParams.pb,
            customMessage: customMessage
        }
    });
}

/**
 * Helper function to generate critical class list map
 * @param jssCriticalClassList The critical class list array
 * @returns jssCriticalClassListMap The map of critical class list
 */
function getJssCriticalClassListMap(jssCriticalClassList: [string?]) {
    const jssCriticalClassListMap: { [className: string]: boolean } = {};
    jssCriticalClassList.forEach((key) => {
        jssCriticalClassListMap[key] = true;
        jssCriticalClassListMap[key + " - jssStyleSheet"] = true;
    });

    return jssCriticalClassListMap;
}